import { Component, computed, inject, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { NetworkService } from '../../../../core/services/network.service';
import { NeedType, PublicProfile } from '../../../../core/models';
import { NEED_TYPE_CODES, REGION_CODES, SECTOR_CODES } from '../../../../core/constants/referentials';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

const MAX_FETCH_SIZE = 100;
const DISPLAY_PAGE_SIZE = 8;

const CARD_COLORS = ['#3A7A2B', '#2563EB', '#B01D30', '#8A6414', '#0F172A', '#6A2FB0'];

function colorFor(seed: string): string {
  let hash = 0;
  for (const char of seed) hash = char.charCodeAt(0) + ((hash << 5) - hash);
  return CARD_COLORS[Math.abs(hash) % CARD_COLORS.length];
}

/**
 * UC-29/30 : annuaire public "Startups & TPME" (profile-service, GET /profiles/search).
 * Comme la cartographie (voir organizations/cartography.ts), cet endpoint exige en réalité un
 * utilisateur authentifié malgré la doc Swagger — un visiteur anonyme voit une invite de connexion.
 *
 * Un seul accountType par appel côté backend (ProfileAdminController#searchPublic) : "Startups &
 * TPME" combine donc deux recherches en parallèle (STARTUP, TPME) plutôt qu'un seul appel, même
 * limitation déjà rencontrée et documentée dans SearchService (UC-03).
 *
 * Filtres Région/Secteur/Besoin tous réels : régionCode filtré côté client sur le jeu déjà chargé
 * (comme la cartographie), secteur et besoin relancent une recherche serveur (needType est un
 * paramètre réel de /profiles/search, jusqu'ici non exploité côté frontend).
 */
@Component({
  selector: 'app-startups-tpme',
  imports: [RouterLink, ScrollReveal, TranslatePipe],
  templateUrl: './startups-tpme.html',
  styleUrl: './startups-tpme.css',
})
export class StartupsTpme {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly networkService = inject(NetworkService);

  readonly isAuthenticated = this.auth.isAuthenticated;
  /** Codes (referentials.ts) → clé i18n `regions.*`/`sectors.*`/`needs.*`. */
  readonly regionOptions = Object.values(REGION_CODES);
  readonly sectorOptions = Object.values(SECTOR_CODES);
  readonly needOptions = Object.values(NEED_TYPE_CODES);

  readonly regionFilter = signal('');
  readonly sectorFilter = signal('');
  readonly needFilter = signal('');

  readonly loading = signal(false);
  readonly error = signal(false);
  readonly allMatches = signal<PublicProfile[]>([]);
  readonly pageSize = DISPLAY_PAGE_SIZE;
  readonly currentPage = signal(0);

  readonly filteredResults = computed(() => {
    const region = this.regionFilter();
    return region ? this.allMatches().filter((p) => p.regionCode === region) : this.allMatches();
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredResults().length / this.pageSize)));
  readonly paginatedResults = computed(() => {
    const start = this.currentPage() * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredResults().slice(start, end);
  });

  /** Code région/secteur → clé i18n (`regions.*`/`sectors.*`), sinon null (le template retombe alors sur le code brut). */
  readonly regionLabelKey = (regionCode: string | null) => (regionCode ? `regions.${regionCode}` : null);
  readonly sectorLabelKey = (sectorCode: string | null | undefined) => (sectorCode ? `sectors.${sectorCode}` : null);

  constructor() {
    if (this.isAuthenticated()) this.search();
  }

  goToLogin(): void {
    void this.router.navigate(['/auth/login']);
  }

  setRegionFilter(region: string): void {
    this.regionFilter.set(region);
    this.currentPage.set(0);
  }

  setSectorFilter(sector: string): void {
    this.sectorFilter.set(sector);
    this.currentPage.set(0);
    this.search();
  }

  setNeedFilter(need: string): void {
    this.needFilter.set(need);
    this.currentPage.set(0);
    this.search();
  }

  setPage(page: number): void {
    const totalPages = this.totalPages();
    const safePage = Math.max(0, Math.min(page, totalPages - 1));
    this.currentPage.set(safePage);
  }

  resetFilters(): void {
    this.regionFilter.set('');
    this.sectorFilter.set('');
    this.needFilter.set('');
    this.currentPage.set(0);
    this.search();
  }

  initials(displayName: string): string {
    return displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  colorFor(profile: PublicProfile): string {
    return colorFor(profile.organizationProfile?.sectorCode ?? profile.id);
  }

  organizationName(profile: PublicProfile): string {
    return profile.organizationProfile?.legalName || profile.organizationProfile?.tradeName || profile.displayName;
  }

  description(profile: PublicProfile): string | null {
    return profile.organizationProfile?.description || profile.bio || profile.headline;
  }

  private search(): void {
    if (!this.isAuthenticated()) return;
    this.loading.set(true);
    this.error.set(false);

    const filters = {
      sectorCode: this.sectorFilter() || undefined,
      needType: (this.needFilter() || undefined) as NeedType | undefined,
      size: MAX_FETCH_SIZE,
    };

    forkJoin({
      startups: this.networkService.searchProfiles({ ...filters, accountType: 'STARTUP' }),
      tpme: this.networkService.searchProfiles({ ...filters, accountType: 'TPME' }),
    }).subscribe({
      next: ({ startups, tpme }) => {
        this.allMatches.set([...startups.content, ...tpme.content]);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
  }
}
